// src/scripts/useScripts/useForecasts.js
import { computed, ref } from "vue";
import { db } from "@/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  writeBatch,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";

import { MAX_WEEKS_FORWARD } from "@/scripts/sharedHelpers/weeklyLimits";

// ✅ formatter.js hard rule: reuse existing formatter utilities.
// We DO NOT assume function names. We import the module and safely fall back.
import * as formatter from "@/scripts/sharedHelpers/formatter";

/* =========================
   Constants
   ========================= */

const CATEGORIES = ["liquor", "food", "door", "other"];
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

/* =========================
   Date helpers (week math only)
   ========================= */

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

// Local ISO date helper (ONLY used if formatter doesn't provide one)
function toISODateLocal(d) {
  const x = new Date(d);
  const yyyy = x.getFullYear();
  const mm = String(x.getMonth() + 1).padStart(2, "0");
  const dd = String(x.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

// Prefer formatter.js if it exists
function toISODate(d) {
  // try common names without assuming one specific export
  const f =
    formatter.toISODate ||
    formatter.toISO ||
    formatter.formatISODate ||
    formatter.isoDate ||
    null;

  return typeof f === "function" ? f(d) : toISODateLocal(d);
}

// Prefer formatter.js if it exists
function formatUK(d) {
  const f =
    formatter.formatUK ||
    formatter.formatUKDate ||
    formatter.ukDate ||
    formatter.formatDateUK ||
    null;

  if (typeof f === "function") return f(d);

  // fallback
  const x = new Date(d);
  const dd = String(x.getDate()).padStart(2, "0");
  const mm = String(x.getMonth() + 1).padStart(2, "0");
  const yyyy = x.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

// Week ending = Sunday. If today is Sunday, it is current W/E. Otherwise next Sunday.
function getCurrentWeekEnding(today = new Date()) {
  const d = startOfDay(today);
  const day = d.getDay(); // 0=Sun
  const delta = day === 0 ? 0 : 7 - day;
  return addDays(d, delta);
}

// Given a week-ending (Sunday), return array of Mon..Sun dates for that week.
function getWeekDates(weekEndingDate) {
  const we = startOfDay(weekEndingDate);
  const mon = addDays(we, -6);
  return DAYS.map((_, idx) => addDays(mon, idx));
}

function blankWeekModel(weekEndingDateISO) {
  const weekEndingDate = new Date(weekEndingDateISO);
  const dates = getWeekDates(weekEndingDate);
  const rows = {};
  for (const cat of CATEGORIES) rows[cat] = dates.map(() => 0);
  return { weekEndingDateISO, dates, rows };
}

// Difference in whole weeks between two ISO dates that are aligned to week-ending Sundays
function diffWeeks(fromISO, toISO) {
  const from = startOfDay(new Date(fromISO));
  const to = startOfDay(new Date(toISO));
  const diffDays = Math.round((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
  return Math.round(diffDays / 7);
}

// Firestore where-in supports max 10
function chunk(arr, size = 10) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

/* =========================
   Firestore paths
   ========================= */

function weekDocRef(venueId, weekEndingISO) {
  return doc(db, "venues", venueId, "forecast_weeks", weekEndingISO);
}
function cellDocRef(venueId, weekEndingISO, category, dayIndex) {
  return doc(db, "venues", venueId, "forecast_weeks", weekEndingISO, "cells", `${category}_${dayIndex}`);
}
function stateDocRef(venueId) {
  return doc(db, "venues", venueId, "forecast_meta", "state");
}

/* =========================
   Composable
   ========================= */

export function useForecasts({ venueIdRef, isAdminRef, userRef }) {
  const loading = ref(false);
  const error = ref("");
  const weeks = ref([]);
  const statusByWeek = ref({});
  const adjustMode = ref({}); // { [weekEndingISO]: boolean }

  const lockState = ref({
    current_week_ending: null,
    locked_through_week_ending: null,
  });

  const currentWE = computed(() => getCurrentWeekEnding(new Date()));

  // Always show current WE+0 .. WE+MAX_WEEKS_FORWARD
  const baseWeeks = computed(() => {
    const we = currentWE.value;
    const list = [];
    const max = Number(MAX_WEEKS_FORWARD || 0);
    for (let i = 0; i <= max; i++) list.push(toISODate(addDays(we, 7 * i)));
    return list;
  });

  // ✅ "Within and including current week ending +2" = WE+0, WE+1, WE+2
  function isInCurrentPlus2Window(weekEndingISO) {
    const st = lockState.value;
    if (st?.current_week_ending && st?.locked_through_week_ending) {
      return weekEndingISO >= st.current_week_ending && weekEndingISO <= st.locked_through_week_ending;
    }

    // UI fallback only
    const currentWEISO = toISODate(currentWE.value);
    const weeksAhead = diffWeeks(currentWEISO, weekEndingISO);
    return weeksAhead >= 0 && weeksAhead <= 2;
  }

  function getStatus(weekEndingISO) {
    return statusByWeek.value[weekEndingISO] || null;
  }

  function isSubmitted(weekEndingISO) {
    const st = getStatus(weekEndingISO);
    return !!st?.submitted_at;
  }

  function canSubmit(weekEndingISO) {
    if (!venueIdRef.value) return false;
    if (!isAdminRef.value && isInCurrentPlus2Window(weekEndingISO)) return false;
    return !isSubmitted(weekEndingISO);
  }

  function canAdjust(weekEndingISO) {
    if (!venueIdRef.value) return false;
    if (!isAdminRef.value && isInCurrentPlus2Window(weekEndingISO)) return false;
    return isSubmitted(weekEndingISO);
  }

  function isEditable(weekEndingISO) {
    if (!isAdminRef.value && isInCurrentPlus2Window(weekEndingISO)) return false;
    if (!isSubmitted(weekEndingISO)) return true;
    return !!adjustMode.value[weekEndingISO];
  }

  function setAdjustMode(weekEndingISO, enabled) {
    adjustMode.value = { ...adjustMode.value, [weekEndingISO]: enabled };
  }

  async function loadLockState() {
    if (!venueIdRef.value) return;

    try {
      const snap = await getDoc(stateDocRef(venueIdRef.value));
      if (!snap.exists()) {
        lockState.value = { current_week_ending: null, locked_through_week_ending: null };
        return;
      }
      const d = snap.data() || {};
      lockState.value = {
        current_week_ending: typeof d.current_week_ending === "string" ? d.current_week_ending : null,
        locked_through_week_ending: typeof d.locked_through_week_ending === "string" ? d.locked_through_week_ending : null,
      };
    } catch (e) {
      // non-fatal
      console.warn("[useForecasts] loadLockState failed:", e?.message || e);
      lockState.value = { current_week_ending: null, locked_through_week_ending: null };
    }
  }

  async function loadStatuses() {
    if (!venueIdRef.value) return;
    loading.value = true;
    error.value = "";

    try {
      await loadLockState();

      const weekList = baseWeeks.value;
      const map = {};

      for (const part of chunk(weekList, 10)) {
        const qy = query(
          collection(db, "venues", venueIdRef.value, "forecast_weeks"),
          where("week_ending_date", "in", part),
        );

        const snap = await getDocs(qy);
        snap.forEach((docSnap) => {
          const row = docSnap.data() || {};
          const key = typeof row.week_ending_date === "string" ? row.week_ending_date : docSnap.id;

          map[key] = {
            venue_id: venueIdRef.value,
            week_ending_date: key,
            submitted_at: row.submitted_at || null,
            submitted_by_uid: row.submitted_by_uid || null,
            submitted_by_email: row.submitted_by_email || null,
            last_adjusted_at: row.last_adjusted_at || null,
          };
        });
      }

      statusByWeek.value = map;
    } catch (e) {
      error.value = e?.message || String(e);
    } finally {
      loading.value = false;
    }
  }

  async function loadWeekValues() {
    if (!venueIdRef.value) return;
    loading.value = true;
    error.value = "";

    try {
      const weekModels = baseWeeks.value.map((weISO) => blankWeekModel(weISO));

      for (const wk of weekModels) {
        const cellsCol = collection(db, "venues", venueIdRef.value, "forecast_weeks", wk.weekEndingDateISO, "cells");
        const snap = await getDocs(cellsCol);

        snap.forEach((docSnap) => {
          const c = docSnap.data() || {};
          const category = String(c.category || "");
          const dayIndex = Number(c.day_index);
          if (!CATEGORIES.includes(category)) return;
          if (!Number.isFinite(dayIndex) || dayIndex < 0 || dayIndex > 6) return;

          wk.rows[category][dayIndex] = Number(c.value || 0);
        });
      }

      weeks.value = weekModels;
    } catch (e) {
      error.value = e?.message || String(e);
    } finally {
      loading.value = false;
    }
  }

  async function refreshAll() {
    await loadStatuses();
    await loadWeekValues();
  }

  async function persistCell(weekEndingISO, category, dayIndex, value) {
    if (!venueIdRef.value) return;
    if (!isEditable(weekEndingISO)) return;

    const dates = getWeekDates(new Date(weekEndingISO));
    const tradingISO = toISODate(dates[dayIndex]);

    await setDoc(
      cellDocRef(venueIdRef.value, weekEndingISO, category, dayIndex),
      {
        week_ending_date: weekEndingISO,
        category,
        day_index: dayIndex,
        trading_date: tradingISO,
        value: Number(value || 0),
        updated_at: serverTimestamp(),
        updated_by_uid: userRef.value?.uid || null,
        updated_by_email: userRef.value?.email || null,
      },
      { merge: true },
    );

    // Ensure parent exists (for status lookups / patterns)
    await setDoc(
      weekDocRef(venueIdRef.value, weekEndingISO),
      {
        week_ending_date: weekEndingISO,
        updated_at: serverTimestamp(),
        updated_by_uid: userRef.value?.uid || null,
        updated_by_email: userRef.value?.email || null,
        created_at: serverTimestamp(),
      },
      { merge: true },
    );
  }

  function setCell(weekEndingISO, category, dayIndex, value) {
    const wk = weeks.value.find((w) => w.weekEndingDateISO === weekEndingISO);
    if (!wk) return;

    wk.rows[category][dayIndex] = value;

    persistCell(weekEndingISO, category, dayIndex, value).catch((e) => {
      error.value = e?.message || String(e);
    });
  }

  async function submitWeek(weekEndingISO) {
    if (!venueIdRef.value) return;
    if (!canSubmit(weekEndingISO)) return;

    loading.value = true;
    error.value = "";

    try {
      const wk = weeks.value.find((w) => w.weekEndingDateISO === weekEndingISO);
      if (!wk) throw new Error("Week model not found.");

      await setDoc(
        weekDocRef(venueIdRef.value, weekEndingISO),
        {
          week_ending_date: weekEndingISO,
          submitted_at: serverTimestamp(),
          submitted_by_uid: userRef.value?.uid || null,
          submitted_by_email: userRef.value?.email || null,
          updated_at: serverTimestamp(),
          updated_by_uid: userRef.value?.uid || null,
          updated_by_email: userRef.value?.email || null,
          created_at: serverTimestamp(),
        },
        { merge: true },
      );

      const batch = writeBatch(db);

      wk.dates.forEach((dateObj, dayIdx) => {
        const tradingISO = toISODate(dateObj);

        for (const cat of CATEGORIES) {
          batch.set(
            cellDocRef(venueIdRef.value, weekEndingISO, cat, dayIdx),
            {
              week_ending_date: weekEndingISO,
              category: cat,
              day_index: dayIdx,
              trading_date: tradingISO,
              value: Number(wk.rows[cat][dayIdx] || 0),
              updated_at: serverTimestamp(),
              updated_by_uid: userRef.value?.uid || null,
              updated_by_email: userRef.value?.email || null,
            },
            { merge: true },
          );
        }
      });

      await batch.commit();

      setAdjustMode(weekEndingISO, false);
      await refreshAll();
    } catch (e) {
      error.value = e?.message || String(e);
    } finally {
      loading.value = false;
    }
  }

  async function enableAdjust(weekEndingISO) {
    if (!canAdjust(weekEndingISO)) return;

    setAdjustMode(weekEndingISO, true);

    try {
      await setDoc(
        weekDocRef(venueIdRef.value, weekEndingISO),
        {
          week_ending_date: weekEndingISO,
          last_adjusted_at: serverTimestamp(),
          updated_at: serverTimestamp(),
          updated_by_uid: userRef.value?.uid || null,
          updated_by_email: userRef.value?.email || null,
          created_at: serverTimestamp(),
        },
        { merge: true },
      );
    } catch (e) {
      error.value = e?.message || String(e);
    }
  }

  async function disableAdjust(weekEndingISO) {
    setAdjustMode(weekEndingISO, false);
  }

  return {
    CATEGORIES,
    DAYS,
    formatUK,

    loading,
    error,
    weeks,
    statusByWeek,
    currentWE: computed(() => toISODate(currentWE.value)),
    baseWeeks,

    isInCurrentPlus2Window,
    isSubmitted,
    canSubmit,
    canAdjust,
    isEditable,

    refreshAll,
    setCell,
    submitWeek,
    enableAdjust,
    disableAdjust,
  };
}

export async function fetchForecastWeekDailyTotals({ venue_id, week_ending_date }) {
  if (!venue_id) throw new Error("Missing venue_id");
  if (!week_ending_date) throw new Error("Missing week_ending_date");

  const cellsCol = collection(db, "venues", venue_id, "forecast_weeks", week_ending_date, "cells");
  const snap = await getDocs(cellsCol);

  const sums = DAYS.map(() => 0);

  snap.forEach((docSnap) => {
    const c = docSnap.data() || {};
    const dayIndex = Number(c.day_index);
    const v = Number(c.value || 0);

    if (!Number.isFinite(dayIndex) || dayIndex < 0 || dayIndex > 6) return;
    if (!Number.isFinite(v)) return;

    sums[dayIndex] += v;
  });

  return sums;
}